<footer class="w-full relative mt-20">

  <!-- Footer Image Banner - Overlapping with reduced width and pushed down -->
  <div class="relative z-10 w-full flex justify-center"
       >
    <img
      src="/images/footer-image.png"
      alt="Contact Banner"
      class="h-auto object-contain max-w-full"
      [style.width]="(isHandset$ | async) ? '91.67%' : (isTablet$ | async) ? '83.33%' : '75%'"
    />
  </div>

  <!-- Main Footer Section - Adjust negative margin to match padding increase -->
  <section class="bg-primary text-on-primary relative pt-8 lg:pt-24"
           [class.-mt-8]="(isHandset$ | async)"
           [class.-mt-10]="(isTablet$ | async) && !(isHandset$ | async)"
           [class.-mt-24]="!(isTablet$ | async) && !(isHandset$ | async)">
    <div
      class="max-w-7xl mx-auto pb-12"
      [class.px-4]="(isHandset$ | async)"
      [class.px-6]="(isTablet$ | async) && !(isHandset$ | async)"
      [class.px-8]="!((isTablet$ | async) || (isHandset$ | async))">

      <!-- === GRID CONTAINER === -->
      <div
        class="grid gap-8 lg:gap-12 mb-8"
        [class.grid-cols-1]="(isHandset$ | async)"
        [class.grid-cols-2]="(isLessThanMD$ | async) && !(isHandset$ | async)"
        [class.grid-cols-[1fr_auto_auto]]="!(isLessThanMD$ | async)">

        <!-- === COLUMN 1: Company Info === -->
        <div>
          <div class="mb-4">
            <img
              src="/logo_noya.png"
              alt="NOYA Energie Photovoltaïque"
              class="h-12 w-auto brightness-0 invert"
            />
          </div>

          <p
            class="space-y-2 mb-6 opacity-90 leading-relaxed"
            [class.text-xs]="(isHandset$ | async)"
            [class.text-sm]="!(isHandset$ | async)">
            <span class="block">{{ desc1() }}</span>
            <span class="block">{{ desc2() }}</span>
            <span class="block">{{ desc3() }}</span>
          </p>

          <p
            class="mb-4 opacity-90"
            [class.text-xs]="(isHandset$ | async)"
            [class.text-sm]="!(isHandset$ | async)">
            {{ address() }}
          </p>

          <div class="flex gap-3">
            @for (social of socialLinks(); track social.name) {
              <a
                [href]="social.url"
                [attr.aria-label]="social.name"
                class="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110">
                @if (social.icon === 'facebook') {
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                }
                @if (social.icon === 'instagram') {
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069z"/>
                  </svg>
                }
                @if (social.icon === 'youtube') {
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                }
              </a>
            }
          </div>
        </div>

        <!-- === COLUMNS 2 & 3: Navigation + Services (Desktop layout) === -->
        @if (!(isLessThanMD$ | async)) {
          <div class="col-span-2 grid grid-cols-2 gap-16">
            <!-- Navigation -->
            <div class="flex flex-col">
              <h4 class="font-bold mb-4 text-lg">{{ navTitle() }}</h4>
              <ul class="space-y-4">
                @for (link of navigationLinks(); track link.label) {
                  <li>
                    <a
                      [href]="link.url"
                      class="opacity-90 hover:opacity-100 transition-opacity text-sm">
                      {{ link.label }}
                    </a>
                  </li>
                }
              </ul>
            </div>

            <!-- Services -->
            <div class="flex flex-col">
              <h4 class="font-bold mb-4 text-lg">{{ servicesTitle() }}</h4>
              <ul class="space-y-4">
                @for (link of servicesLinks(); track link.label) {
                  <li>
                    <a
                      [href]="link.url"
                      class="opacity-90 hover:opacity-100 transition-opacity text-sm">
                      {{ link.label }}
                    </a>
                  </li>
                }
              </ul>
            </div>
          </div>
        } @else {
          <!-- === Tablet/Mobile fallback: stack columns === -->
          <div>
            <h4 class="font-bold mb-4 text-lg">{{ navTitle() }}</h4>
            <ul class="space-y-2">
              @for (link of navigationLinks(); track link.label) {
                <li>
                  <a
                    [href]="link.url"
                    class="opacity-90 hover:opacity-100 transition-opacity text-sm">
                    {{ link.label }}
                  </a>
                </li>
              }
            </ul>
          </div>

          <div>
            <h4 class="font-bold mb-4 text-lg">{{ servicesTitle() }}</h4>
            <ul class="space-y-2">
              @for (link of servicesLinks(); track link.label) {
                <li>
                  <a
                    [href]="link.url"
                    class="opacity-90 hover:opacity-100 transition-opacity text-sm">
                    {{ link.label }}
                  </a>
                </li>
              }
            </ul>
          </div>
        }
      </div>

      <!-- === COPYRIGHT === -->
      <div class="pt-6 border-t border-white/20 text-center">
        <p
          class="opacity-75"
          [class.text-xs]="(isHandset$ | async)"
          [class.text-sm]="!(isHandset$ | async)">
          Copyright © {{ currentYear() }} {{ copyright() }} |
          <a href="https://bramasquare.com/" target="_blank" rel="noopener noreferrer"
             class="hover:opacity-100 underline underline-offset-2 transition-opacity">
            bramasquare360
          </a>
        </p>
      </div>
    </div>
  </section>
</footer>
